import {useEffect, useState} from "react";
import * as localforage from "localforage";
import {
    EBirdChecklistByFamily,
    EBirdMyDataSchema,
    EBirdObservationsByLocation,
    EBirdSightingsCalibrationResult,
} from "ebird-mydata-reader";
import {parseObservationDateTime} from "./helpers";
import {buildSpeciesDetailPaths} from "./speciesDetailPaths";
import {DEBUG_PERF, endPerfTimer, startPerfTimer} from "./perf";
import {
    DASHBOARD_SUMMARY_CACHE_KEY,
    DASHBOARD_SUMMARY_CACHE_VERSION,
    DashboardInProgressAchievement,
    DashboardRecentActivity,
    DashboardRecentAchievement,
    DashboardSummary,
    DashboardYearStats
} from "./birdDataCache";
import {buildHotspotDetailPath} from "../views/hotspot-explorer/helpers";
import {
    buildAchievementDashboard,
    buildAchievementEnrichmentSignature,
    formatAchievementDate,
    formatAchievementValue
} from "../views/discover/achievements/helpers";
import type {EnrichedObservationsByLocation} from "./types";
import {buildObservationDatasetSignature, PRE_EBIRD_LIST_BUILDING_YEAR} from "./storedBirdData";
import {getYearSpeciesKey, getYearSpeciesObservations} from "./yearSpeciesCounts";

const getChecklistId = (observation: EBirdMyDataSchema) => {
    return observation.submissionID ?? observation.submissionId ?? `${observation.date}-${observation.time}-${observation.location}`;
}

const getNumYears = (years: Array<number|string>) => {
    if (!years.length) return 0;

    const calendarYears = years
        .map(year => parseInt(year as string))
        .filter(year => Number.isInteger(year) && year > PRE_EBIRD_LIST_BUILDING_YEAR);
    if (!calendarYears.length) return years.includes(PRE_EBIRD_LIST_BUILDING_YEAR) || years.includes(PRE_EBIRD_LIST_BUILDING_YEAR.toString()) ? 1 : 0;

    const firstYear = Math.max(...calendarYears);
    const lastYear = Math.min(...calendarYears);
    if (Number.isNaN(firstYear) || Number.isNaN(lastYear)) return years.length;

    return Math.max(firstYear - lastYear, 1);
}

const buildYearStats = (rawData: EBirdMyDataSchema[], calibrationResult?: EBirdSightingsCalibrationResult) => {
    const yearBuckets = new Map<number, {
        species: Set<string|number>,
        locations: Set<string>,
        days: Set<string>,
        lifers: Set<string|number>
    }>();

    for (const observation of rawData) {
        const observedYear = observation.observedYear;
        if (!observedYear) continue;

        if (!yearBuckets.has(observedYear)) {
            yearBuckets.set(observedYear, {
                species: new Set<string|number>(),
                locations: new Set<string>(),
                days: new Set<string>(),
                lifers: new Set<string|number>()
            });
        }

        const bucket = yearBuckets.get(observedYear);
        if (!bucket) continue;

        const locationKey = observation.locationId ?? observation.location;

        if (locationKey) bucket.locations.add(locationKey);
        if (observation.date) bucket.days.add(observation.date);
    }

    for (const [year, bucket] of Array.from(yearBuckets.entries())) {
        for (const observation of getYearSpeciesObservations(rawData, year, calibrationResult)) {
            const speciesKey = getYearSpeciesKey(observation);
            if (speciesKey) bucket.species.add(speciesKey);
            if (observation.isLifer && speciesKey) bucket.lifers.add(speciesKey);
        }
    }

    const yearStatsByYear: Record<string, DashboardYearStats> = {};
    for (const [year, bucket] of Array.from(yearBuckets.entries())) {
        yearStatsByYear[year] = {
            species: bucket.species.size,
            locations: bucket.locations.size,
            days: bucket.days.size,
            lifers: bucket.lifers.size
        };
    }

    return yearStatsByYear;
}

const buildRecentActivity = (
    rawData: EBirdMyDataSchema[],
    speciesDetailPathByTaxonomicOrder: Record<string, string>
) => {
    const checklistBuckets = new Map<string, {
        id: string,
        timestamp: number,
        dateLabel: string,
        location: string,
        locationId?: string,
        county?: string,
        stateProvince?: string,
        speciesByKey: Map<string, {
            label: string,
            detailPath?: string,
            score: number,
            taxonomicOrder: number
        }>
    }>();

    for (const observation of rawData) {
        if (!observation.date) continue;

        const observationDate = parseObservationDateTime(observation.date, observation.time);
        const observationTimestamp = observationDate?.toMillis() ?? 0;

        const checklistId = getChecklistId(observation);
        const existingBucket = checklistBuckets.get(checklistId);
        const location = observation.location || 'Unknown location';

        if (!existingBucket) {
            checklistBuckets.set(checklistId, {
                id: checklistId,
                timestamp: observationTimestamp,
                dateLabel: observationDate?.isValid
                    ? observationDate.toLocaleString({month: 'short', day: 'numeric', year: 'numeric'})
                    : observation.date ?? '',
                location,
                locationId: observation.locationId,
                county: observation.county,
                stateProvince: observation.stateProvince,
                speciesByKey: new Map()
            });
        }

        const bucket = checklistBuckets.get(checklistId);
        if (!bucket) continue;

        if (observationTimestamp > bucket.timestamp) {
            bucket.timestamp = observationTimestamp;
            bucket.dateLabel = observationDate?.isValid
                ? observationDate.toLocaleString({month: 'short', day: 'numeric', year: 'numeric'})
                : observation.date ?? bucket.dateLabel;
        }

        if (!bucket.locationId && observation.locationId) bucket.locationId = observation.locationId;
        if (!bucket.county && observation.county) bucket.county = observation.county;
        if (!bucket.stateProvince && observation.stateProvince) bucket.stateProvince = observation.stateProvince;

        const speciesKey = observation.taxonomicOrder?.toString() || observation.commonName;
        if (!speciesKey || !observation.commonName) continue;

        const score = (observation.isLifer ? 100 : 0) + (observation.isFirstOfYear ? 10 : 0);
        const existingSpecies = bucket.speciesByKey.get(speciesKey);

        if (!existingSpecies || score > existingSpecies.score) {
            bucket.speciesByKey.set(speciesKey, {
                label: observation.commonName,
                detailPath: observation.taxonomicOrder
                    ? speciesDetailPathByTaxonomicOrder[observation.taxonomicOrder]
                    : undefined,
                score,
                taxonomicOrder: observation.taxonomicOrder ?? Number.MAX_SAFE_INTEGER
            });
        }
    }

    const latestChecklistByLocation = new Map<string, DashboardRecentActivity>();

    for (const bucket of Array.from(checklistBuckets.values())) {
        const locationKey = bucket.locationId ?? bucket.location;
        const activity: DashboardRecentActivity = {
            id: bucket.id,
            dateLabel: bucket.dateLabel,
            location: bucket.location,
            locationDetailPath: bucket.locationId ? buildHotspotDetailPath(bucket.locationId) : undefined,
            regionLabel: [bucket.county, bucket.stateProvince].filter(Boolean).join(', '),
            species: Array.from(bucket.speciesByKey.values())
                .filter(species => {
                    if (!species.detailPath) return false;
                    return !/\bsp\./i.test(species.label);
                })
                .sort((a, b) => {
                    if (b.score !== a.score) return b.score - a.score;
                    if (a.taxonomicOrder !== b.taxonomicOrder) return a.taxonomicOrder - b.taxonomicOrder;
                    return a.label.localeCompare(b.label);
                })
                .slice(0, 5)
                .map(species => ({
                    label: species.label,
                    detailPath: species.detailPath
                })),
            timestamp: bucket.timestamp
        };

        const existingActivity = latestChecklistByLocation.get(locationKey);
        if (!existingActivity || activity.timestamp > existingActivity.timestamp) {
            latestChecklistByLocation.set(locationKey, activity);
        }
    }

    return Array.from(latestChecklistByLocation.values())
        .sort((a, b) => b.timestamp - a.timestamp)
        .slice(0, 3);
}

const buildDashboardSummary = (
    rawData: EBirdMyDataSchema[],
    lifeList: EBirdMyDataSchema[],
    observationsByLocation: Array<EBirdObservationsByLocation | EnrichedObservationsByLocation>,
    checklist: EBirdChecklistByFamily[],
    years: Array<number|string>,
    calibrationResult: EBirdSightingsCalibrationResult | undefined,
    sourceSignature: string
): DashboardSummary => {
    const totalTimer = startPerfTimer('dashboard:buildSummary');
    const pathTimer = startPerfTimer('dashboard:buildSpeciesDetailPaths');
    const speciesDetailPathByTaxonomicOrder = buildSpeciesDetailPaths(checklist);
    endPerfTimer(pathTimer);
    const yearStatsTimer = startPerfTimer('dashboard:buildYearStats');
    const yearStatsByYear = buildYearStats(rawData, calibrationResult);
    endPerfTimer(yearStatsTimer);
    const recentActivityTimer = startPerfTimer('dashboard:buildRecentActivity');
    const recentActivity = buildRecentActivity(rawData, speciesDetailPathByTaxonomicOrder);
    endPerfTimer(recentActivityTimer);
    const achievementTimer = startPerfTimer('dashboard:buildAchievements');
    const achievementDashboard = buildAchievementDashboard({
        rawData,
        calibrationResult,
        lifeList,
        observationsByLocation,
        checklist
    });
    const recentAchievements: DashboardRecentAchievement[] = achievementDashboard.recentEarned
        .slice(0, 2)
        .map(achievement => ({
            id: achievement.id,
            setId: achievement.setId,
            color: achievement.color,
            title: achievement.title,
            description: achievement.description,
            earnedDateLabel: formatAchievementDate(achievement.earnedDate)
        }));
    const inProgressAchievements: DashboardInProgressAchievement[] = achievementDashboard.inProgress
        .slice(0, 2)
        .map(achievement => ({
            id: achievement.id,
            setId: achievement.setId,
            color: achievement.color,
            title: achievement.title,
            description: achievement.description,
            progressLabel: `${formatAchievementValue(achievement.currentValue)} / ${formatAchievementValue(achievement.threshold)}`,
            percent: achievement.percent
        }));
    endPerfTimer(achievementTimer);

    const summary = {
        version: DASHBOARD_SUMMARY_CACHE_VERSION,
        sourceSignature,
        speciesDetailPathByTaxonomicOrder,
        yearStatsByYear,
        recentActivity,
        recentAchievements,
        inProgressAchievements,
        numYears: getNumYears(years)
    };
    endPerfTimer(totalTimer);

    return summary;
}

export function useDashboardSummary(
    rawData: EBirdMyDataSchema[],
    lifeList: EBirdMyDataSchema[],
    observationsByLocation: Array<EBirdObservationsByLocation | EnrichedObservationsByLocation>,
    checklist: EBirdChecklistByFamily[],
    years: Array<number|string>,
    calibrationResult?: EBirdSightingsCalibrationResult,
    calibrationCacheKey = 'uncalibrated',
    enabled = true
) {
    const [dashboardSummary, setDashboardSummary] = useState<DashboardSummary>();
    const [isDashboardSummaryLoading, setIsDashboardSummaryLoading] = useState(false);

    useEffect(() => {
        let isActive = true;

        if (!enabled || !rawData.length || !checklist.length) {
            setDashboardSummary(undefined);
            setIsDashboardSummaryLoading(false);
            return () => {
                isActive = false;
            };
        }

        setDashboardSummary(undefined);
        setIsDashboardSummaryLoading(true);

        const resolveDashboardSummary = async () => {
            const enrichmentSignature = buildAchievementEnrichmentSignature(observationsByLocation);
            const sourceSignature = `${buildObservationDatasetSignature(rawData)}:${calibrationCacheKey}:${enrichmentSignature}`;
            const totalTimer = startPerfTimer('dashboard:resolveSummary');
            try {
                const cacheTimer = startPerfTimer('dashboard:cacheLookup');
                const cachedSummary: DashboardSummary | null = await localforage.getItem(DASHBOARD_SUMMARY_CACHE_KEY);
                endPerfTimer(cacheTimer);
                if (!isActive) return;

                if (
                    cachedSummary?.version === DASHBOARD_SUMMARY_CACHE_VERSION
                    && cachedSummary.sourceSignature === sourceSignature
                    && cachedSummary.recentActivity?.length
                    && Array.isArray(cachedSummary.recentAchievements)
                    && Array.isArray(cachedSummary.inProgressAchievements)
                    && Object.keys(cachedSummary.yearStatsByYear ?? {}).length
                ) {
                    if (DEBUG_PERF) console.debug('dashboard:cacheHit', {
                        recentActivityCount: cachedSummary.recentActivity.length,
                        yearCount: Object.keys(cachedSummary.yearStatsByYear ?? {}).length
                    });
                    if (!isActive) return;

                    setDashboardSummary(cachedSummary);
                    return;
                }

                if (DEBUG_PERF) console.debug('dashboard:cacheMiss');
                const nextSummary = buildDashboardSummary(rawData, lifeList, observationsByLocation, checklist, years, calibrationResult, sourceSignature);
                if (!isActive) return;
                const cacheWriteTimer = startPerfTimer('dashboard:cacheWrite');
                await localforage.setItem(DASHBOARD_SUMMARY_CACHE_KEY, nextSummary);
                endPerfTimer(cacheWriteTimer);

                if (!isActive) return;
                setDashboardSummary(nextSummary);
            } catch (error) {
                console.error(error);

                if (DEBUG_PERF) console.debug('dashboard:fallbackRebuild');
                const nextSummary = buildDashboardSummary(rawData, lifeList, observationsByLocation, checklist, years, calibrationResult, sourceSignature);
                if (!isActive) return;
                setDashboardSummary(nextSummary);
            } finally {
                endPerfTimer(totalTimer);
                if (isActive) setIsDashboardSummaryLoading(false);
            }
        }

        // Let React paint the loading state before the synchronous summary build begins.
        const timeoutId = window.setTimeout(() => {
            void resolveDashboardSummary();
        }, 0);

        return () => {
            isActive = false;
            window.clearTimeout(timeoutId);
        }
    }, [DASHBOARD_SUMMARY_CACHE_VERSION, calibrationCacheKey, calibrationResult, checklist, enabled, lifeList, observationsByLocation, rawData, years]);

    return {dashboardSummary, isDashboardSummaryLoading};
}
